import { useState, useEffect } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

const RESET_PASSWORD_URL = "https://dimwpgeuqvtzlrlvveme.supabase.co/functions/v1/reset-password";

export default function ResetPasswordPage() {
  const { isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setMessage("Geçersiz veya eksik sıfırlama bağlantısı.");
    }
  }, [token]);

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("loading");
    setMessage("");

    if (!password.trim() || !confirmPassword.trim()) {
      setStatus("error");
      setMessage("Tüm alanları doldurunuz.");
      return;
    }

    if (password.length < 6) {
      setStatus("error");
      setMessage("Şifre en az 6 karakter olmalıdır.");
      return;
    }

    if (password !== confirmPassword) {
      setStatus("error");
      setMessage("Şifreler eşleşmiyor.");
      return;
    }

    try {
      const response = await fetch(RESET_PASSWORD_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string}`,
        },
        body: JSON.stringify({ token, newPassword: password }),
      });

      const data = await response.json();

      if (data.success) {
        setStatus("success");
        setMessage(data.message);
      } else {
        setStatus("error");
        setMessage(data.message || "Bir hata oluştu.");
      }
    } catch {
      setStatus("error");
      setMessage("Bağlantı hatası. Lütfen tekrar deneyin.");
    }
  };

  if (!token && status === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50 px-4">
        <div className="w-full max-w-md">
          <div className="bg-background-50 border border-background-200/70 rounded-2xl p-8 text-center">
            <div className="bg-accent-100 border border-accent-300 text-accent-900 rounded-lg px-4 py-3 text-sm mb-4">
              <i className="ri-error-warning-line text-lg block mb-1"></i>
              {message}
            </div>
            <a
              href="/login"
              className="inline-block text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line mr-1"></i>
              Giriş sayfasına dön
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background-50 px-4">
      <div className="w-full max-w-md">
        <div className="bg-background-50 border border-background-200/70 rounded-2xl p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-primary-500 rounded-xl flex items-center justify-center mx-auto mb-4">
              <i className="ri-shield-check-line text-2xl text-background-50"></i>
            </div>
            <h1 className="text-2xl font-bold text-foreground-950 font-heading">Yeni Şifre</h1>
            <p className="text-foreground-600 mt-1 text-sm">Yeni şifrenizi belirleyin.</p>
          </div>

          {status === "success" ? (
            <div className="text-center space-y-4">
              <div className="bg-accent-100 border border-accent-300 text-accent-900 rounded-lg px-4 py-3 text-sm">
                <i className="ri-checkbox-circle-line text-lg block mb-1"></i>
                {message}
              </div>
              <a
                href="/login"
                className="inline-block text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line mr-1"></i>
                Giriş sayfasına dön
              </a>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                  Yeni Şifre
                </label>
                <div className="relative">
                  <i className="ri-lock-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-lg"></i>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="En az 6 karakter"
                    className="w-full pl-10 pr-10 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer"
                  >
                    <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                  Şifre Tekrar
                </label>
                <div className="relative">
                  <i className="ri-lock-password-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-lg"></i>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Şifrenizi tekrar girin"
                    className="w-full pl-10 pr-4 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                    autoComplete="new-password"
                  />
                </div>
              </div>

              {status === "error" && (
                <div className="bg-accent-100 border border-accent-300 text-accent-900 rounded-lg px-4 py-2.5 text-sm flex items-center gap-2">
                  <i className="ri-error-warning-line text-base shrink-0"></i>
                  {message}
                </div>
              )}

              <button
                type="submit"
                disabled={status === "loading"}
                className="w-full bg-primary-500 hover:bg-primary-600 text-background-50 font-medium py-2.5 rounded-lg text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                {status === "loading" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Güncelleniyor...
                  </>
                ) : (
                  <>
                    <i className="ri-check-line"></i>
                    Şifreyi Güncelle
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}